import Phaser from 'phaser';
import { TilesetManager } from '../managers/TilesetManager';
import { PixelRainRenderer } from '../systems/PixelRainRenderer';
import { COLORS } from '../types';

// Constants
const TILE_SIZE = 16;
const TICK_RATE = 5;
const TICK_MS = 1000 / TICK_RATE;
const OCEAN_ANIMATION_FRAMES = 5;
const OCEAN_ANIMATION_SPEED = 150; // ms per frame
const OCEAN_DECOR_CHANCE = 0.02; // 2% chance for rocks
const ISLAND_DECOR_CHANCE = 0.09; // 9% chance for island decor
const CLOUD_OVERLAY_ALPHA = 0.15; // Subtle cloud overlay

// Grid colors matching the game
const GRID_COLORS = {
  thin: 0x1e3a5f,
  thick: 0x2a4a6f,
  alpha: 0.4
};

// Biome to tile mapping
const BIOME_TILE_MAP: Record<string, { shore: string; main: string; accent: string; feature: string }> = {
  beach:    { shore: 'beach_shore', main: 'beach_main', accent: 'beach_accent', feature: 'beach_feature' },
  forest:   { shore: 'forest_shore', main: 'forest_main', accent: 'forest_accent', feature: 'forest_feature' },
  mountain: { shore: 'mountain_shore', main: 'mountain_main', accent: 'mountain_accent', feature: 'mountain_feature' },
  jungle:   { shore: 'jungle_shore', main: 'jungle_main', accent: 'jungle_accent', feature: 'jungle_feature' },
  desert:   { shore: 'desert_shore', main: 'desert_main', accent: 'desert_accent', feature: 'desert_feature' },
  volcanic: { shore: 'volcanic_shore', main: 'volcanic_main', accent: 'volcanic_accent', feature: 'volcanic_feature' },
  winter:   { shore: 'winter_shore', main: 'winter_main', accent: 'winter_accent', feature: 'winter_feature' },
  mixed:    { shore: 'mixed_shore', main: 'mixed_main', accent: 'mixed_accent', feature: 'mixed_feature' },
};


interface IslandData {
  id: string;
  x: number;
  y: number;
  radiusTiles: number;
  biome: string;
}

interface AmbientNPC {
  sprite: Phaser.GameObjects.Container;
  tileX: number;
  tileY: number;
  path: Array<{ x: number; y: number }>;
  pathIndex: number;
  color: number;
  moveCounter?: number;
}

interface WaterTile {
  sprite: Phaser.GameObjects.Sprite;
  tileX: number;
  tileY: number;
}

class SeededRandom {
  private seed: number;
  constructor(seed: number) {
    this.seed = seed;
  }
  next(): number {
    this.seed = (this.seed * 1664525 + 1013904223) & 0xffffffff;
    return (this.seed >>> 0) / 0xffffffff;
  }
  nextFloat(min: number, max: number): number {
    return this.next() * (max - min) + min;
  }
  nextInt(min: number, max: number): number {
    return Math.floor(this.nextFloat(min, max + 1));
  }
}

class WaterRenderer {
  private scene: Phaser.Scene;
  private tilesetManager: TilesetManager | null = null;
  private tiles: Map<string, WaterTile> = new Map();
  private animationTimer: number = 0;
  private currentFrame: number = 0;

  constructor(scene: Phaser.Scene, tilesetManager: TilesetManager) {
    this.scene = scene;
    this.tilesetManager = tilesetManager;
  }

  createWaterTiles() {
    if (!this.tilesetManager) return;

    const screenWidth = this.scene.scale.width;
    const screenHeight = this.scene.scale.height;
    const tilesX = Math.ceil(screenWidth / TILE_SIZE) + 2;
    const tilesY = Math.ceil(screenHeight / TILE_SIZE) + 2;

    for (let ty = 0; ty < tilesY; ty++) {
      for (let tx = 0; tx < tilesX; tx++) {
        const textureKey = this.tilesetManager.getTileTexture('ocean_animation', this.currentFrame);
        if (!textureKey) continue;

        const sprite = this.scene.add.sprite(
          tx * TILE_SIZE,
          ty * TILE_SIZE,
          textureKey
        ).setOrigin(0);
        
        sprite.setDepth(-5);
        
        const key = `${tx},${ty}`;
        this.tiles.set(key, { sprite, tileX: tx, tileY: ty });
      }
    }
  }

  update(delta: number) {
    if (!this.tilesetManager) return;

    this.animationTimer += delta;
    if (this.animationTimer >= OCEAN_ANIMATION_SPEED) {
      this.animationTimer = 0;
      this.currentFrame = (this.currentFrame + 1) % OCEAN_ANIMATION_FRAMES;

      const textureKey = this.tilesetManager.getTileTexture('ocean_animation', this.currentFrame);
      if (textureKey) {
        this.tiles.forEach(tile => {
          tile.sprite.setTexture(textureKey);
        });
      }
    }
  }

  destroy() {
    this.tiles.forEach(tile => tile.sprite.destroy());
    this.tiles.clear();
  }
}

export class LoginWorldScene extends Phaser.Scene {
  private tilesetManager: TilesetManager | null = null;
  private gridGraphics: Phaser.GameObjects.Graphics | null = null;
  private waterRenderer: WaterRenderer | null = null;
  private npcs: AmbientNPC[] = [];
  private islands: IslandData[] = [];
  private islandSprites: Map<string, Phaser.GameObjects.Sprite> = new Map();
  private islandTiles: Map<string, Set<string>> = new Map(); // island id -> set of tile keys
  private oceanDecorSprites: Map<string, Phaser.GameObjects.Sprite> = new Map();
  private islandDecorSprites: Map<string, Phaser.GameObjects.Sprite> = new Map();
  private cloudOverlay: Phaser.GameObjects.Graphics | null = null;
  private oceanOverlay: Phaser.GameObjects.Rectangle | null = null;
  private pixelRainRenderer: PixelRainRenderer | null = null;
  private blockedTiles: Set<string> = new Set();
  private oceanTiles: Array<{ x: number; y: number }> = [];
  private tickAccumulator: number = 0;
  private rng: SeededRandom = new SeededRandom(12345);
  private biomes = ['beach', 'forest', 'mountain', 'jungle', 'desert', 'volcanic', 'winter', 'mixed'];

  constructor() {
    super({ key: 'LoginWorldScene' });
  }

  preload(): void {
    this.tilesetManager = new TilesetManager(this);
    this.tilesetManager.preloadTilesets();
  }

  create(): void {
    if (!this.tilesetManager) {
      console.error('❌ TilesetManager not initialized');
      return;
    }

    // Set camera zoom to 2x to match main game visual scale
    this.cameras.main.setZoom(2);

    this.waterRenderer = new WaterRenderer(this, this.tilesetManager);
    this.waterRenderer.createWaterTiles();

    // Add ocean darkening overlay for depth effect (matches main game)
    const screenWidth = this.scale.width;
    const screenHeight = this.scale.height;
    this.oceanOverlay = this.add.rectangle(
      screenWidth / 2,
      screenHeight / 2,
      screenWidth * 3,
      screenHeight * 3,
      COLORS.oceanOverlay,
      0.45
    );
    this.oceanOverlay.setOrigin(0.5, 0.5);
    this.oceanOverlay.setDepth(-5);
    this.oceanOverlay.setScrollFactor(0, 0);

    // Add storm rain effect
    this.pixelRainRenderer = new PixelRainRenderer(this);

    this.gridGraphics = this.add.graphics();
    this.gridGraphics.setDepth(-4);
    
    this.generateIslands();
    this.drawIslands();
    this.addOceanDecor();
    this.drawGrid();
    this.spawnNPCs();
    
    this.events.on('update', this.update, this);
  }

  private generateIslands(): void {
    const islandCount = 3;
    const padding = 100;
    const width = this.scale.width;
    const height = this.scale.height;
    const minDistance = 200; // Minimum distance between island centers

    let attempts = 0;
    const maxAttempts = 100;

    while (this.islands.length < islandCount && attempts < maxAttempts) {
      attempts++;
      const x = this.rng.nextFloat(padding, width - padding);
      const y = this.rng.nextFloat(padding, height - padding);
      const radiusTiles = this.rng.nextInt(8, 12);
      
      // Check if this island would overlap with existing islands
      let tooClose = false;
      for (const existing of this.islands) {
        const dx = x - existing.x;
        const dy = y - existing.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const minRequired = (radiusTiles + existing.radiusTiles) * TILE_SIZE + minDistance;
        
        if (distance < minRequired) {
          tooClose = true;
          break;
        }
      }
      
      if (!tooClose) {
        const biome = this.biomes[this.rng.nextInt(0, this.biomes.length - 1)];
        this.islands.push({ id: `login_island_${this.islands.length}`, x, y, radiusTiles, biome });
      }
    }
  }

  private drawIslands(): void {
    if (!this.tilesetManager) return;

    for (const island of this.islands) {
      this.renderRadiusIsland(island);
    }
  }

  private renderRadiusIsland(island: IslandData): void {
    if (!this.tilesetManager) return;
    
    const tileMap = BIOME_TILE_MAP[island.biome] || BIOME_TILE_MAP.mixed;
    const centerTileX = Math.floor(island.x / TILE_SIZE);
    const centerTileY = Math.floor(island.y / TILE_SIZE);
    const seed = Math.floor(island.x * 1000 + island.y);
    
    const seededRandom = (offset: number): number => {
      const xRand = Math.sin(seed + offset) * 10000;
      return xRand - Math.floor(xRand);
    };

    const radiusTiles = island.radiusTiles;
    const islandTileSet = new Set<string>();

    // First pass: collect all island tiles
    for (let dy = -radiusTiles; dy <= radiusTiles; dy++) {
      for (let dx = -radiusTiles; dx <= radiusTiles; dx++) {
        const dist = Math.sqrt(dx * dx + dy * dy);
        const noise = seededRandom(dx * 100 + dy) * 0.5;
        const threshold = radiusTiles * (0.8 + noise * 0.4);

        if (dist <= threshold) {
          const tx = centerTileX + dx;
          const ty = centerTileY + dy;
          islandTileSet.add(`${tx},${ty}`);
          this.blockedTiles.add(`${tx},${ty}`);
        }
      }
    }

    const prunedIslandTiles = this.pruneThreeSideTiles(islandTileSet);
    islandTileSet.forEach(tileKey => {
      if (!prunedIslandTiles.has(tileKey)) {
        this.blockedTiles.delete(tileKey);
      }
    });
    prunedIslandTiles.forEach(tileKey => {
      this.blockedTiles.add(tileKey);
    });

    this.islandTiles.set(island.id, prunedIslandTiles);

    // Second pass: render tiles with proper shoreline detection
    for (let dy = -radiusTiles; dy <= radiusTiles; dy++) {
      for (let dx = -radiusTiles; dx <= radiusTiles; dx++) {
        const tx = centerTileX + dx;
        const ty = centerTileY + dy;
        const tileKey = `${tx},${ty}`;

        if (!prunedIslandTiles.has(tileKey)) continue;

        // Check for water neighbors to determine if this is a shore tile
        const hasWaterNeighbor = 
          !prunedIslandTiles.has(`${tx},${ty - 1}`) ||
          !prunedIslandTiles.has(`${tx + 1},${ty}`) ||
          !prunedIslandTiles.has(`${tx},${ty + 1}`) ||
          !prunedIslandTiles.has(`${tx - 1},${ty}`) ||
          !prunedIslandTiles.has(`${tx + 1},${ty - 1}`) ||
          !prunedIslandTiles.has(`${tx + 1},${ty + 1}`) ||
          !prunedIslandTiles.has(`${tx - 1},${ty + 1}`) ||
          !prunedIslandTiles.has(`${tx - 1},${ty - 1}`);

        let tileType: string;

        if (hasWaterNeighbor) {
          // Use shoreline variant
          tileType = this.getShorelineTileType(island.biome, tx, ty, prunedIslandTiles);
        } else {
          // Interior tile
          tileType = seededRandom(dx * 30 + dy * 30) > 0.5 ? tileMap.main : tileMap.accent;
        }

        const worldX = tx * TILE_SIZE;
        const worldY = ty * TILE_SIZE;

        const textureKey = this.tilesetManager.getTileTexture(tileType);
        if (textureKey) {
          const sprite = this.add.sprite(worldX, worldY, textureKey).setOrigin(0);
          sprite.setDepth(-3);
          this.islandSprites.set(tileKey, sprite);
        }

        // Add island decor on interior tiles
        if (!hasWaterNeighbor) {
          const decorSeed = seededRandom(dx * 777 + dy * 999);
          if (decorSeed < ISLAND_DECOR_CHANCE) {
            const decorIndex = Math.floor(decorSeed * 10000) % 8;
            const decorKey = `island_decor_${String(decorIndex + 1).padStart(2, '0')}`;
            if (this.tilesetManager.hasDirectTexture(decorKey)) {
              const decorSprite = this.add.sprite(worldX + TILE_SIZE / 2, worldY + TILE_SIZE / 2, decorKey);
              decorSprite.setOrigin(0.5);
              decorSprite.setDepth(-2);
              this.islandDecorSprites.set(`${tileKey}_decor`, decorSprite);
            }
          }
        }
      }
    }
  }

  private getShorelineTileType(biome: string, tileX: number, tileY: number, islandTiles: Set<string>): string {
    const bitmask = this.calculateShorelineBitmask(tileX, tileY, islandTiles);
    
    if (bitmask === 0) {
      return `${biome}_shore`;
    }

    const variant = this.getShorelineVariantFromBitmask(bitmask);
    
    if (variant) {
      return `${biome}_shore_${variant}`;
    }
    
    return `${biome}_shore`;
  }

  private calculateShorelineBitmask(tileX: number, tileY: number, islandTiles: Set<string>): number {
    let bitmask = 0;
    
    const n = islandTiles.has(`${tileX},${tileY - 1}`);
    const ne = islandTiles.has(`${tileX + 1},${tileY - 1}`);
    const e = islandTiles.has(`${tileX + 1},${tileY}`);
    const se = islandTiles.has(`${tileX + 1},${tileY + 1}`);
    const s = islandTiles.has(`${tileX},${tileY + 1}`);
    const sw = islandTiles.has(`${tileX - 1},${tileY + 1}`);
    const w = islandTiles.has(`${tileX - 1},${tileY}`);
    const nw = islandTiles.has(`${tileX - 1},${tileY - 1}`);
    
    if (!n) bitmask |= 1;
    if (!ne) bitmask |= 2;
    if (!e) bitmask |= 4;
    if (!se) bitmask |= 8;
    if (!s) bitmask |= 16;
    if (!sw) bitmask |= 32;
    if (!w) bitmask |= 64;
    if (!nw) bitmask |= 128;
    
    return bitmask;
  }

  private getShorelineVariantFromBitmask(bitmask: number): string | null {
    const hasN = (bitmask & 1) !== 0;
    const hasNE = (bitmask & 2) !== 0;
    const hasE = (bitmask & 4) !== 0;
    const hasSE = (bitmask & 8) !== 0;
    const hasS = (bitmask & 16) !== 0;
    const hasSW = (bitmask & 32) !== 0;
    const hasW = (bitmask & 64) !== 0;
    const hasNW = (bitmask & 128) !== 0;

    const waterCardinals = [hasN, hasE, hasS, hasW].filter(Boolean).length;
    const waterDiagonals = [hasNE, hasSE, hasSW, hasNW].filter(Boolean).length;

    // Inner corners
    if (waterDiagonals === 1 && hasNE && !hasN && !hasE) return 'inner_sw';
    if (waterDiagonals === 1 && hasSE && !hasS && !hasE) return 'inner_nw';
    if (waterDiagonals === 1 && hasSW && !hasS && !hasW) return 'inner_ne';
    if (waterDiagonals === 1 && hasNW && !hasN && !hasW) return 'inner_se';

    if (waterCardinals === 1) {
      if (hasN) return 'n';
      if (hasE) return 'e';
      if (hasS) return 's';
      if (hasW) return 'w';
    }

    if (waterCardinals === 2) {
      if (hasN && hasE) return 'ne';
      if (hasE && hasS) return 'se';
      if (hasS && hasW) return 'sw';
      if (hasW && hasN) return 'nw';

      if (hasN && hasS) return 'n';
      if (hasE && hasW) return 'e';
    }

    if (waterCardinals >= 3) {
      return null;
    }

    if (hasNE) return 'ne';
    if (hasSE) return 'se';
    if (hasSW) return 'sw';
    if (hasNW) return 'nw';

    return null;
  }

  private pruneThreeSideTiles(insideTiles: Set<string>): Set<string> {
    const prunedTiles: Set<string> = new Set(insideTiles);
    const toRemove: Array<string> = [];

    prunedTiles.forEach(key => {
      const [txStr, tyStr] = key.split(',');
      const tx = Number(txStr);
      const ty = Number(tyStr);

      const waterCardinals =
        (!prunedTiles.has(`${tx},${ty - 1}`) ? 1 : 0) +
        (!prunedTiles.has(`${tx + 1},${ty}`) ? 1 : 0) +
        (!prunedTiles.has(`${tx},${ty + 1}`) ? 1 : 0) +
        (!prunedTiles.has(`${tx - 1},${ty}`) ? 1 : 0);

      if (waterCardinals >= 3) {
        toRemove.push(key);
      }
    });

    toRemove.forEach(key => prunedTiles.delete(key));

    return prunedTiles;
  }

  private addOceanDecor(): void {
    if (!this.tilesetManager) return;

    const screenWidth = this.scale.width;
    const screenHeight = this.scale.height;
    const tilesX = Math.ceil(screenWidth / TILE_SIZE);
    const tilesY = Math.ceil(screenHeight / TILE_SIZE);

    for (let ty = 0; ty < tilesY; ty++) {
      for (let tx = 0; tx < tilesX; tx++) {
        if (this.blockedTiles.has(`${tx},${ty}`)) continue;

        const decorSeed = this.getDeterministicRandom(tx, ty);
        if (decorSeed < OCEAN_DECOR_CHANCE) {
          const variantIndex = Math.floor(decorSeed * 10000) % 12;
          const variant = `ocean_rock_${String(variantIndex + 1).padStart(2, '0')}`;
          const texture = this.tilesetManager.getTileTexture(variant);
          
          if (texture) {
            const worldX = tx * TILE_SIZE + TILE_SIZE / 2;
            const worldY = ty * TILE_SIZE + TILE_SIZE / 2;
            const sprite = this.add.sprite(worldX, worldY, texture);
            sprite.setOrigin(0.5);
            sprite.setDepth(3);
            const scale = 0.75 + (decorSeed * 4000) % 0.25;
            sprite.setScale(scale);
            this.oceanDecorSprites.set(`${tx},${ty}`, sprite);
          }
        }
      }
    }
  }

  private addCloudOverlay(): void {
    const screenWidth = this.scale.width;
    const screenHeight = this.scale.height;

    this.cloudOverlay = this.add.graphics();
    this.cloudOverlay.setDepth(10);

    // Create subtle cloud patches
    for (let i = 0; i < 8; i++) {
      const x = this.rng.nextFloat(0, screenWidth);
      const y = this.rng.nextFloat(0, screenHeight);
      const radius = this.rng.nextFloat(80, 150);
      
      this.cloudOverlay.fillStyle(0xffffff, CLOUD_OVERLAY_ALPHA);
      this.cloudOverlay.fillCircle(x, y, radius);
    }
  }

  private getDeterministicRandom(x: number, y: number): number {
    const seed = x * 73856093 ^ y * 19349663;
    const xRand = Math.sin(seed) * 10000;
    return xRand - Math.floor(xRand);
  }

  private drawGrid(): void {
    if (!this.gridGraphics) return;
    
    const width = this.scale.width;
    const height = this.scale.height;
    
    this.gridGraphics.clear();
    
    this.gridGraphics.lineStyle(1, GRID_COLORS.thin, GRID_COLORS.alpha * 0.25);
    
    for (let x = 0; x <= width; x += TILE_SIZE) {
      this.gridGraphics.moveTo(x, 0);
      this.gridGraphics.lineTo(x, height);
    }
    
    for (let y = 0; y <= height; y += TILE_SIZE) {
      this.gridGraphics.moveTo(0, y);
      this.gridGraphics.lineTo(width, y);
    }
    
    this.gridGraphics.lineStyle(1, GRID_COLORS.thick, GRID_COLORS.alpha * 0.5);
    const majorGridSize = TILE_SIZE * 4;
    
    for (let x = 0; x <= width; x += majorGridSize) {
      this.gridGraphics.moveTo(x, 0);
      this.gridGraphics.lineTo(x, height);
    }
    
    for (let y = 0; y <= height; y += majorGridSize) {
      this.gridGraphics.moveTo(0, y);
      this.gridGraphics.lineTo(width, y);
    }
    
    this.gridGraphics.strokePath();
  }

  private spawnNPCs(): void {
    const NPC_FACTION_COLORS = [0x3b82f6, 0xef4444, 0x10b981, 0x8b5cf6]; // Navy, Pirate, Merchant, Smuggler
    const screenWidth = this.scale.width;
    const screenHeight = this.scale.height;
    const tilesX = Math.ceil(screenWidth / TILE_SIZE);
    const tilesY = Math.ceil(screenHeight / TILE_SIZE);

    for (let ty = 0; ty < tilesY; ty++) {
      for (let tx = 0; tx < tilesX; tx++) {
        if (!this.blockedTiles.has(`${tx},${ty}`)) {
          this.oceanTiles.push({ x: tx, y: ty });
        }
      }
    }

    const npcCount = Math.min(this.oceanTiles.length, 3);
    const shipTypes = ['sloop', 'brigantine', 'galleon'];
    for (let i = 0; i < npcCount; i++) {
      const tile = this.oceanTiles[Math.floor(Math.random() * this.oceanTiles.length)];
      const color = NPC_FACTION_COLORS[i % NPC_FACTION_COLORS.length];
      const npcId = `login_npc_${i}`;
      const shipType = shipTypes[i % shipTypes.length];

      const shipContainer = this.createShipVisual(shipType, TILE_SIZE * 1.5, color, npcId);
      shipContainer.setPosition(
        tile.x * TILE_SIZE + TILE_SIZE / 2,
        tile.y * TILE_SIZE + TILE_SIZE / 2
      );
      shipContainer.setDepth(2);

      const npc: AmbientNPC = {
        sprite: shipContainer,
        tileX: tile.x,
        tileY: tile.y,
        path: [],
        pathIndex: 0,
        color
      };

      this.generatePath(npc);
      this.npcs.push(npc);
    }
  }

  private createShipVisual(shipType: string, finalSize: number, color: number, shipId: string): Phaser.GameObjects.Container {
    const shipContainer = this.add.container(0, 0);
    const w = finalSize / 2;
    const h = finalSize / 2;
    
    switch (shipType) {
      case 'brigantine':
        this.createBrigantineVisual(shipContainer, w, h, color, shipId);
        break;
      case 'galleon':
        this.createGalleonVisual(shipContainer, w, h, color, shipId);
        break;
      case 'sloop':
      default:
        this.createSloopVisual(shipContainer, w, h, color, shipId);
    }
    
    return shipContainer;
  }

  private createSloopVisual(container: Phaser.GameObjects.Container, w: number, h: number, color: number, shipId: string): void {
    // Sloop - small, fast, single-masted ship
    const hash = Math.abs(shipId.split('').reduce((h, c) => ((h << 5) - h) + c.charCodeAt(0), 0));
    const colorVar = hash % 4;
    const sizeVar = (hash >> 8) % 3;
    
    const hullColors = [0x5c4033, 0x6b4d3a, 0x4f3a2a, 0x5a4231];
    const deckColors = [0x8b6f47, 0x9a7d52, 0x7d6340, 0x8a7249];
    const cabinColors = [0x6b5344, 0x7a5f4d, 0x5f4a3b, 0x6d5a48];
    const sailScale = [0.85, 1.0, 1.15][sizeVar];
    
    const hullColor = hullColors[colorVar];
    const deckColor = deckColors[colorVar];
    const cabinColor = cabinColors[colorVar];
    
    // Hull - dark wood base (elongated)
    const hullBase = this.add.rectangle(0, h * 0.4, w * 1.8, h * 1.0, hullColor);
    container.add(hullBase);
    
    // Deck - lighter wood on top
    const deck = this.add.rectangle(0, h * 0.1, w * 1.6, h * 0.6, deckColor);
    container.add(deck);
    
    // Cabin/Quarterdeck - small structure
    const cabin = this.add.rectangle(0, h * 0.6, w * 0.8, h * 0.4, cabinColor);
    container.add(cabin);
    
    // Stern - rectangular protrusion on LEFT side
    const stern = this.add.rectangle(-w * 0.9, h * 0.4, w * 0.3, h * 0.6, 0x4a3426);
    container.add(stern);
    
    // Bow - rectangular protrusion on RIGHT side
    const bow = this.add.rectangle(w * 0.9, h * 0.4, w * 0.4, h * 0.8, 0x3d2817);
    container.add(bow);

    this.addPointyProw(container, w * 1.05, h * 0.4, w, h);
    
    // Mast - dark wood pole (thin vertical rectangle)
    const mast = this.add.rectangle(0, -h * 0.8, 2, h * 1.5, 0x3d2817);
    container.add(mast);
    
    // Sail - white/cream color (rectangle, not triangle)
    const sail = this.add.rectangle(-w * 0.4, -h * 0.9, w * 0.8 * sailScale, h * 0.9 * sailScale, 0xf5f5dc);
    sail.setFillStyle(0xf5f5dc, 1);
    sail.setAlpha(0.95);
    container.add(sail);
    
    // Flag - faction color at top of mast
    const flag = this.add.rectangle(w * 0.1, -h * 1.5, w * 0.5, h * 0.3, color);
    container.add(flag);
  }

  private createBrigantineVisual(container: Phaser.GameObjects.Container, w: number, h: number, color: number, shipId: string): void {
    // Brigantine - medium, two-masted ship
    const hash = Math.abs(shipId.split('').reduce((h, c) => ((h << 5) - h) + c.charCodeAt(0), 0));
    const colorVar = hash % 4;
    const sizeVar = (hash >> 8) % 3;
    
    const hullColors = [0x5c4033, 0x6b4d3a, 0x4f3a2a, 0x5a4231];
    const deckColors = [0x8b6f47, 0x9a7d52, 0x7d6340, 0x8a7249];
    const cabinColors = [0x6b5344, 0x7a5f4d, 0x5f4a3b, 0x6d5a48];
    const sailScale = [0.85, 1.0, 1.15][sizeVar];
    
    const hullColor = hullColors[colorVar];
    const deckColor = deckColors[colorVar];
    const cabinColor = cabinColors[colorVar];
    
    // Hull - slightly larger than sloop
    const hullBase = this.add.rectangle(0, h * 0.4, w * 2.0, h * 1.1, hullColor);
    container.add(hullBase);
    
    // Deck
    const deck = this.add.rectangle(0, h * 0.1, w * 1.8, h * 0.7, deckColor);
    container.add(deck);
    
    // Cabin - larger, positioned toward stern (back)
    const cabin = this.add.rectangle(0, h * 0.7, w * 1.0, h * 0.5, cabinColor);
    container.add(cabin);
    
    // Stern - rectangular protrusion on LEFT side
    const stern = this.add.rectangle(-w * 1.0, h * 0.4, w * 0.35, h * 0.7, 0x4a3426);
    container.add(stern);
    
    // Bow - rectangular protrusion on RIGHT side
    const bow = this.add.rectangle(w * 1.0, h * 0.4, w * 0.4, h * 0.9, 0x3d2817);
    container.add(bow);

    this.addPointyProw(container, w * 1.10, h * 0.4, w, h);
    
    // Two masts (thin vertical rectangles)
    const mast1 = this.add.rectangle(-w * 0.3, -h * 0.9, 2, h * 1.6, 0x3d2817);
    container.add(mast1);
    
    const mast2 = this.add.rectangle(w * 0.3, -h * 0.8, 2, h * 1.4, 0x3d2817);
    container.add(mast2);
    
    // Two sails
    const sail1 = this.add.rectangle(-w * 0.6, -h * 1.0, w * 0.7 * sailScale, h * 1.0 * sailScale, 0xf5f5dc);
    sail1.setFillStyle(0xf5f5dc, 1);
    sail1.setAlpha(0.95);
    container.add(sail1);
    
    const sail2 = this.add.rectangle(w * 0.1, -h * 0.8, w * 0.6 * sailScale, h * 0.8 * sailScale, 0xf5f5dc);
    sail2.setFillStyle(0xf5f5dc, 1);
    sail2.setAlpha(0.95);
    container.add(sail2);
    
    // Flag
    const flag = this.add.rectangle(-w * 0.2, -h * 1.6, w * 0.5, h * 0.3, color);
    container.add(flag);
  }

  private createGalleonVisual(container: Phaser.GameObjects.Container, w: number, h: number, color: number, shipId: string): void {
    // Galleon - large, three-masted warship
    const hash = Math.abs(shipId.split('').reduce((h, c) => ((h << 5) - h) + c.charCodeAt(0), 0));
    const colorVar = hash % 4;
    const sizeVar = (hash >> 8) % 3;
    
    const hullColors = [0x5c4033, 0x6b4d3a, 0x4f3a2a, 0x5a4231];
    const deckColors = [0x8b6f47, 0x9a7d52, 0x7d6340, 0x8a7249];
    const cabinColors = [0x6b5344, 0x7a5f4d, 0x5f4a3b, 0x6d5a48];
    const sailScale = [0.85, 1.0, 1.15][sizeVar];
    
    const hullColor = hullColors[colorVar];
    const deckColor = deckColors[colorVar];
    const cabinColor = cabinColors[colorVar];
    
    // Hull - much larger
    const hullBase = this.add.rectangle(0, h * 0.5, w * 2.2, h * 1.3, hullColor);
    container.add(hullBase);
    
    // Deck
    const deck = this.add.rectangle(0, h * 0.2, w * 2.0, h * 0.8, deckColor);
    container.add(deck);
    
    // Large cabin - positioned toward stern
    const cabin = this.add.rectangle(0, h * 0.8, w * 1.2, h * 0.6, cabinColor);
    container.add(cabin);
    
    // Stern - rectangular protrusion on LEFT side
    const stern = this.add.rectangle(-w * 1.1, h * 0.5, w * 0.4, h * 0.8, 0x4a3426);
    container.add(stern);
    
    // Bow - rectangular protrusion on RIGHT side
    const bow = this.add.rectangle(w * 1.1, h * 0.5, w * 0.45, h * 1.0, 0x2d1810);
    container.add(bow);

    this.addPointyProw(container, w * 1.2, h * 0.5, w, h);
    
    // Three masts (thin vertical rectangles)
    const mast1 = this.add.rectangle(-w * 0.4, -h * 1.0, 2, h * 1.8, 0x3d2817);
    container.add(mast1);
    
    const mast2 = this.add.rectangle(0, -h * 0.95, 2, h * 1.7, 0x3d2817);
    container.add(mast2);
    
    const mast3 = this.add.rectangle(w * 0.4, -h * 0.85, 2, h * 1.5, 0x3d2817);
    container.add(mast3);
    
    // Three sails
    const sail1 = this.add.rectangle(-w * 0.8, -h * 1.1, w * 0.8 * sailScale, h * 1.1 * sailScale, 0xf5f5dc);
    sail1.setFillStyle(0xf5f5dc, 1);
    sail1.setAlpha(0.95);
    container.add(sail1);
    
    const sail2 = this.add.rectangle(-w * 0.1, -h * 1.0, w * 0.7 * sailScale, h * 1.0 * sailScale, 0xf5f5dc);
    sail2.setFillStyle(0xf5f5dc, 1);
    sail2.setAlpha(0.95);
    container.add(sail2);
    
    const sail3 = this.add.rectangle(w * 0.5, -h * 0.85, w * 0.6 * sailScale, h * 0.85 * sailScale, 0xf5f5dc);
    sail3.setFillStyle(0xf5f5dc, 1);
    sail3.setAlpha(0.95);
    container.add(sail3);
    
    // Flag
    const flag = this.add.rectangle(-w * 0.3, -h * 1.7, w * 0.5, h * 0.3, color);
    container.add(flag);
  }

  private addPointyProw(container: Phaser.GameObjects.Container, x: number, y: number, w: number, h: number): void {
    const c1 = 0x2d1810;
    const c2 = 0x3d2817;
    const c3 = 0x4a3426;

    const base = this.add.rectangle(x, y, w * 0.34, h * 0.42, c3);
    container.add(base);
    const mid = this.add.rectangle(x + (w * 0.14), y, w * 0.24, h * 0.30, c1);
    container.add(mid);
    const tip = this.add.rectangle(x + (w * 0.26), y, w * 0.14, h * 0.18, c2);
    container.add(tip);
  }

  private lightenColor(color: number, amount: number): number {
    const r = (color >> 16) & 255;
    const g = (color >> 8) & 255;
    const b = color & 255;
    
    const newR = Math.min(255, Math.floor(r + (255 - r) * amount));
    const newG = Math.min(255, Math.floor(g + (255 - g) * amount));
    const newB = Math.min(255, Math.floor(b + (255 - b) * amount));
    
    return (newR << 16) | (newG << 8) | newB;
  }

  private generatePath(npc: AmbientNPC): void {
    if (this.oceanTiles.length === 0) {
      npc.path = [];
      return;
    }

    const pathLength = 3 + Math.floor(Math.random() * 4);
    npc.path = [];
    for (let i = 0; i < pathLength; i++) {
      const tile = this.oceanTiles[Math.floor(Math.random() * this.oceanTiles.length)];
      npc.path.push({ x: tile.x, y: tile.y });
    }
    npc.pathIndex = 0;
  }

  private updateNPCs(): void {
    for (const npc of this.npcs) {
      if (npc.path.length === 0) {
        this.generatePath(npc);
        continue;
      }

      const target = npc.path[npc.pathIndex];
      const dx = target.x - npc.tileX;
      const dy = target.y - npc.tileY;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < 0.5) {
        npc.pathIndex++;
        if (npc.pathIndex >= npc.path.length) {
          this.generatePath(npc);
        }
      } else {
        // Move every 3 ticks (slower, more realistic movement)
        if (!npc.moveCounter) npc.moveCounter = 0;
        npc.moveCounter++;
        
        if (npc.moveCounter >= 3) {
          npc.moveCounter = 0;
          let nextX = npc.tileX;
          let nextY = npc.tileY;

          if (Math.abs(dx) > Math.abs(dy)) {
            nextX += dx > 0 ? 1 : -1;
          } else {
            nextY += dy > 0 ? 1 : -1;
          }

          if (!this.blockedTiles.has(`${nextX},${nextY}`)) {
            npc.tileX = nextX;
            npc.tileY = nextY;
            npc.sprite.setPosition(
              nextX * TILE_SIZE + TILE_SIZE / 2,
              nextY * TILE_SIZE + TILE_SIZE / 2
            );
          }
        }
      }
    }
  }

  update(time: number, delta: number): void {
    if (this.waterRenderer) {
      this.waterRenderer.update(delta);
    }

    // Update rain effect
    if (this.pixelRainRenderer) {
      this.pixelRainRenderer.update(delta);
    }
    
    this.tickAccumulator += delta;
    while (this.tickAccumulator >= TICK_MS) {
      this.tickAccumulator -= TICK_MS;
      this.updateNPCs();
    }
  }

  shutdown(): void {
    if (this.waterRenderer) {
      this.waterRenderer.destroy();
      this.waterRenderer = null;
    }
    
    if (this.gridGraphics) {
      this.gridGraphics.destroy();
      this.gridGraphics = null;
    }

    this.islandSprites.forEach(sprite => sprite.destroy());
    this.islandSprites.clear();
    
    this.oceanDecorSprites.forEach(sprite => sprite.destroy());
    this.oceanDecorSprites.clear();
    
    this.islandDecorSprites.forEach(sprite => sprite.destroy());
    this.islandDecorSprites.clear();
    
    if (this.cloudOverlay) {
      this.cloudOverlay.destroy();
      this.cloudOverlay = null;
    }

    if (this.oceanOverlay) {
      this.oceanOverlay.destroy();
      this.oceanOverlay = null;
    }

    if (this.pixelRainRenderer) {
      this.pixelRainRenderer.destroy();
      this.pixelRainRenderer = null;
    }
    
    this.events.off('update', this.update, this);
  }
}